import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TextField,
} from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorIcon from "@mui/icons-material/Error";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { useCustomFormat } from "../views/after-init";
import { Autocomplete, DatePicker } from "@mhome/ui";
import {
  getHistory,
  getHistoryList,
  listAutomations,
} from "../service/automation-service";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import { useWebSocket } from "../ws/websocket";
import { useLocation } from "react-router-dom";
import LoadingSpinner from "../common/loading";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { useToast } from "../common/toast";
import { useHistory } from "react-router-dom";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { useCustomTheme } from "../theme-shadcn";

const AutomationHistoryList = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const [data, setData] = useState([]);
  const [groupedHistories, setGroupedHistories] = useState({});
  const navigate = useHistory();

  const [expandedHistories, setExpandedHistories] = useState([]);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const customFormat = useCustomFormat();
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const observerRef = useRef(null);
  const hasMore = useRef(true);
  const nextToken = useRef("");
  const minSize = useRef(0);

  const filterDate = useRef("");
  const lastFilterTimestampRef = useRef(null);
  const currentRequestRef = useRef(null);
  const { sendAndWaitWs } = useWebSocket();
  const { successToast, warningToast, errorToast } = useToast();
  const getComponentBgColor = useComponentBackgroundColor();
  const { palette } = useCustomTheme();
  const wsReady = useRecoilValue(wsReadyState);
  const selectedAutomation = useRef(null);

  const urlParams = new URLSearchParams(location.search);
  const [searchInput, setSearchInput] = useState(urlParams.get("filter") || "");
  const [automationOptions, setAutomationOptions] = useState([]);
  const [loadAll, setLoadAll] = useState(true);

  const filterOptions = (options, { inputValue }) => {
    const searchTerms = inputValue
      .toLowerCase()
      .split(" ")
      .filter((term) => term.length > 0);

    return options.filter((option) => {
      const searchText = option.name.toLowerCase();
      return searchTerms.every((term) => searchText.includes(term));
    });
  };

  useEffect(() => {
    if (wsReady) {
      handleListAutomations();
    }
  }, [wsReady]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && !loading && hasMore.current) {
        fetchData(false);
      }
    }, options);

    observerRef.current = observer;

    const observerTarget = document.querySelector(".observer-target");
    if (observerTarget) {
      observer.observe(observerTarget);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) return;
    const grouped = data.reduce((acc, History) => {
      const day = dayjs(History.timestamp).format("YYYY-MM-DD");
      if (!acc[day]) acc[day] = [];
      acc[day].push(History);
      return acc;
    }, {});
    setGroupedHistories(grouped);
    console.debug(grouped);
  }, [data]);

  const handleFilterDateChange = () => {
    if (!filterDate.current) {
      console.debug("Invalid filterDate or filterType, skipping fetch.");
      return;
    }
    setGroupedHistories({});
    setData([]);
    nextToken.current = "";
    fetchData(true);
  };

  const handleListAutomations = async () => {
    setLoadAll(true);
    try {
      const resp = await listAutomations(sendAndWaitWs, "", "");

      if (resp && Array.isArray(resp.automations)) {
        const automations = [
          { id: "", name: "All" },
          ...resp["automations"].map(({ id, name }) => ({
            id,
            name,
          })),
        ];

        setAutomationOptions(automations);

        if (id) {
          const defaultAutomation = automations.find((option) => {
            return option.id === id;
          });

          if (defaultAutomation) {
            selectedAutomation.current = defaultAutomation;
          } else {
            selectedAutomation.current = { id, name: "Unknown" };
          }
        }

        setLoadAll(false);
      } else {
        setAutomationOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
      setAutomationOptions([]);
    }
    setLoadAll(false);
  };

  const fetchData = async (isReset = false) => {
    if (loading || (!hasMore.current && !isReset)) return;

    const filterTimestamp = filterDate.current
      ? dayjs(filterDate.current).endOf("day").valueOf()
      : null;

    if (
      filterTimestamp &&
      filterTimestamp === lastFilterTimestampRef.current &&
      !isReset
    ) {
      console.debug("Filter timestamp has not changed, skipping fetch.");
      return;
    }

    if (isReset) {
      minSize.current = 0;
      setGroupedHistories({});
      setData([]);
      nextToken.current = "";
      setHistoryLoading(true);
      hasMore.current = true;
    } else {
      setLoading(true);
    }

    const requestId = Symbol();
    currentRequestRef.current = requestId;

    try {
      const token = isReset ? "" : nextToken.current;
      console.debug("id", selectedAutomation.current?.id);
      let result = selectedAutomation.current?.id
        ? await getHistory(
            sendAndWaitWs,
            selectedAutomation.current.id,
            20,
            token,
            filterTimestamp === lastFilterTimestampRef.current
              ? ""
              : filterTimestamp
          )
        : await getHistoryList(
            sendAndWaitWs,
            20,
            token,
            filterTimestamp === lastFilterTimestampRef.current
              ? ""
              : filterTimestamp
          );

      if (currentRequestRef.current !== requestId) {
        return;
      }

      lastFilterTimestampRef.current = filterTimestamp;

      if (lastFilterTimestampRef.current !== filterTimestamp) {
        console.debug("Filter timestamp has been updated, ignoring result.");
        return;
      }

      let histories = [];

      if (result.error) {
        hasMore.current = false;
        errorToast("Fetch history error!");
        return;
      }

      if (result.automationHistories) {
        histories = result.automationHistories.map((history) => {
          history.name = result.name;
          return history;
        });
      } else {
        histories = result.automationHistoryEntryList?.flatMap((list) => {
          return list.automationHistories.map((history) => {
            history.name = list.name;
            return history;
          });
        });
      }

      nextToken.current = result.page.nextToken;
      hasMore.current = result.page.hasMore;

      const sortedHistories = (histories || []).sort(
        (a, b) => b.timestamp - a.timestamp
      );

      setData((prev) =>
        isReset ? sortedHistories : [...prev, ...sortedHistories]
      );

      if (result.page.hasMore && minSize.current < 10) {
        minSize.current += histories.length;
        if (minSize.current < 10 && result.page.hasMore) {
          await fetchData(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      if (currentRequestRef.current === requestId) {
        setLoading(false);
        setHistoryLoading(false);
      }
    }
  };

  const handleToggleHistory = (index) => {
    setExpandedHistories((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleAutomationChange = (newValue) => {
    selectedAutomation.current = newValue;
    fetchData(true);
  };

  const handleSearchInputChange = (newInputValue) => {
    setSearchInput(newInputValue);
  };

  return (
    <>
      {loadAll ? (
        <></>
      ) : (
        <div className="p-4" style={{ backgroundColor: "transparent" }}>
          <Card
            className="mb-2"
            style={{
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
            }}
          >
            <div
              className="p-2 gap-3"
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="gap-3"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  width: "100%",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    flex: "1 1 200px",
                    minWidth: "200px",
                    height: "40px",
                  }}
                >
                  <Autocomplete
                    options={automationOptions}
                    getOptionLabel={(option) =>
                      option && option.name ? option.name : ""
                    }
                    value={selectedAutomation.current}
                    onChange={(event, newValue) => {
                      handleAutomationChange(newValue);
                    }}
                    inputValue={searchInput}
                    onInputChange={(event, newInputValue) => {
                      handleSearchInputChange(newInputValue);
                    }}
                    filterOptions={filterOptions}
                    renderInput={(params) => {
                      // Extract style from params to avoid conflicts
                      const {
                        style: paramsStyle,
                        InputProps: paramsInputProps,
                        ...restParams
                      } = params;
                      return (
                        <TextField
                          {...restParams}
                          placeholder="Select Automation"
                          variant="outlined"
                          size="small"
                          style={{
                            width: "100%",
                            height: "40px",
                            ...paramsStyle,
                          }}
                          InputProps={{
                            ...paramsInputProps,
                            style: {
                              ...paramsInputProps?.style,
                              backgroundColor:
                                getComponentBgColor("hsl(var(--card))"),
                              borderRadius: "16px",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                            },
                          }}
                        />
                      );
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </div>

                <div
                  className="gap-2"
                  style={{
                    display: "flex",
                    flex: "1 1 200px",
                    minWidth: "200px",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: "150px",
                      height: "40px",
                    }}
                  >
                    <DatePicker
                      value={
                        filterDate.current ? dayjs(filterDate.current) : null
                      }
                      onChange={(newValue) => {
                        filterDate.current = newValue
                          ? newValue.format("YYYY-MM-DD")
                          : "";
                        if (!newValue) {
                          fetchData(true);
                        } else {
                          handleFilterDateChange();
                        }
                      }}
                      clearable
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "outlined",
                          style: {
                            width: "100%",
                            height: "40px",
                          },
                          InputProps: {
                            style: {
                              width: "100%",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              backgroundColor:
                                getComponentBgColor("hsl(var(--card))"),
                              borderRadius: "16px",
                            },
                          },
                        },
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </div>

                  <IconButton
                    onClick={() => {
                      fetchData(true);
                    }}
                    disabled={loading}
                    style={{
                      height: "40px",
                      width: "40px",
                      backgroundColor: "hsl(var(--background))",
                      borderRadius: "16px",
                      padding: "6px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--accent))";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--background))";
                    }}
                  >
                    <RefreshIcon
                      style={{
                        fontSize: 20,
                        color: loading
                          ? "hsl(var(--muted-foreground))"
                          : "hsl(var(--foreground))",
                        animation: loading ? "spin 1s linear infinite" : "none",
                      }}
                    />
                  </IconButton>
                </div>
              </div>
            </div>
          </Card>

          {historyLoading && (
            <div className="flex justify-center pt-8 pb-8">
              <CircularProgress size={24} />
            </div>
          )}
          {!historyLoading && (
            <>
              {Object.keys(groupedHistories).length > 0 ? (
                Object.keys(groupedHistories).map((day, idx) => (
                  <div key={idx} className="mb-8">
                    <Typography
                      variant="h6"
                      className="mb-2"
                      style={{
                        color: "hsl(var(--foreground))",
                      }}
                    >
                      {day}
                    </Typography>
                    <div
                      className="mb-4"
                      style={{
                        borderBottom: `1px solid hsl(var(--border))`,
                      }}
                    />

                    {groupedHistories[day].map((history, index) => (
                      <Accordion
                        key={index}
                        expanded={expandedHistories.includes(`${day}-${index}`)}
                        onChange={() => {
                          handleToggleHistory(`${day}-${index}`);
                        }}
                        className="mb-2"
                        style={{
                          backgroundColor: "hsl(var(--card))",
                          borderRadius: 16,
                          boxShadow: "none",
                          overflow: "hidden",
                          border: "none",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={false}
                          style={{
                            padding: 0,
                            backgroundColor: "hsl(var(--card))",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              alignItems: "center",
                              width: "100%",
                              gridTemplateColumns:
                                "auto minmax(0,1fr) auto auto",
                              columnGap: "0.75rem",
                              padding: "0.5rem 1rem",
                            }}
                          >
                            {/* Lifecycle indicator */}
                            <div
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor:
                                  history.lifecycle?.toLowerCase() ===
                                  "standard"
                                    ? palette?.background?.l2 || "#9fe3a8"
                                    : palette?.background?.l6 || "#8fbdcf",
                                flexShrink: 0,
                              }}
                            />

                            {/* Main content */}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              <Typography
                                variant="body1"
                                style={{
                                  fontWeight: 600,
                                  color: "hsl(var(--foreground))",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  marginBottom: "0.25rem",
                                }}
                              >
                                {history.name}
                              </Typography>

                              <Typography
                                variant="caption"
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                  fontWeight: 400,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {customFormat(
                                  dayjs(history.timestamp),
                                  currentTime
                                )}
                              </Typography>
                            </div>

                            {/* Error indicator */}
                            {history.logs.some(
                              (log) =>
                                log.level.trim().toLowerCase() === "error" ||
                                (log.level.trim().toLowerCase() === "flag" &&
                                  log.message.toLowerCase() === "error")
                            ) && (
                              <div
                                className="gap-1 pl-2 pr-2 pt-0.5 pb-0.5"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  backgroundColor:
                                    "hsl(var(--destructive) / 0.12)",
                                  color: "hsl(var(--destructive))",
                                  borderRadius: "16px",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  flexShrink: 0,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <ErrorIcon fontSize="inherit" />
                                <Typography
                                  variant="caption"
                                  style={{ fontWeight: "inherit" }}
                                >
                                  Error
                                </Typography>
                              </div>
                            )}

                            {/* Navigation arrow */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                navigate.push(
                                  `/automation/overview?id=${history.automationId}`
                                );
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              style={{
                                backgroundColor: "hsl(var(--background))",
                                width: 32,
                                height: 32,
                                flexShrink: 0,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "hsl(var(--accent))";
                                e.currentTarget.style.transform =
                                  "translateX(2px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "hsl(var(--background))";
                                e.currentTarget.style.transform =
                                  "translateX(0)";
                              }}
                            >
                              <ArrowForwardIcon
                                fontSize="small"
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                }}
                              />
                            </IconButton>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails
                          style={{
                            paddingTop: "0.5rem",
                            paddingBottom: "0.5rem",
                            paddingLeft: "1rem",
                            paddingRight: "1rem",
                            backgroundColor: "hsl(var(--card))",
                          }}
                        >
                          {history.logs
                            .filter(
                              (log) => log.level.trim().toLowerCase() !== "flag"
                            )
                            .map((log, index) => (
                              <div
                                key={log.id || index}
                                className="pt-1 pb-1"
                                style={{
                                  borderBottom:
                                    index <
                                    history.logs.filter(
                                      (l) =>
                                        l.level.trim().toLowerCase() !== "flag"
                                    ).length -
                                      1
                                      ? `1px solid hsl(var(--border) / 0.12)`
                                      : "none",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  style={{
                                    color: "hsl(var(--muted-foreground))",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {customFormat(
                                    dayjs(log.timestamp),
                                    currentTime
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  component="div"
                                  style={{
                                    wordWrap: "break-word",
                                    whiteSpace: "pre-wrap",
                                    color: "hsl(var(--foreground))",
                                    fontSize: "0.85rem",
                                    lineHeight: 1.4,
                                    marginTop: "0.125rem",
                                  }}
                                >
                                  <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                    {log.message}
                                  </ReactMarkdown>
                                </Typography>
                              </div>
                            ))}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </div>
                ))
              ) : (
                <Card
                  className="p-6 flex flex-col items-center justify-center"
                  style={{
                    boxShadow: "none",
                  }}
                >
                  <Typography
                    variant="h6"
                    className="mb-2"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                    }}
                  >
                    No histories available
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                      textAlign: "center",
                    }}
                  >
                    There are no automation histories to display for the
                    selected filters.
                  </Typography>
                </Card>
              )}
            </>
          )}
          <div
            className="observer-target"
            ref={(el) => {
              if (el && observerRef.current) {
                observerRef.current.observe(el);
              }
            }}
            style={{
              height: "20px",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {loading && !historyLoading && <LoadingSpinner />}
          </div>
          {!loading &&
            !hasMore.current &&
            Object.keys(groupedHistories).length > 0 && (
              <Typography
                variant="body2"
                className="text-center p-4"
                style={{
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                No more activities
              </Typography>
            )}
        </div>
      )}
    </>
  );
};

export default AutomationHistoryList;
